import { Transition } from "@headlessui/react";
import { InputHTMLAttributes, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface Props extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  label?: string;
  className?: string;
  disabled?: boolean;
  value: string;
  invalid?: boolean;
  onChange: (value: string) => void;
  onEnter?: (value: string) => void;
  enterPrompt?: boolean;
  isActive?: boolean;
  isPassword?: boolean;
  optional?: boolean;
  required?: boolean;
}

type InputState = 'isInvalid' | 'isDisabled' | 'isActive' | 'default';

function TextInputField(props: Props) {
  const { value, onChange, className, onEnter, enterPrompt, isActive, isPassword, invalid, ...rest } = props;
  const id = useId();
  const ref = useRef<HTMLInputElement>(null);

  const { t } = useTranslation();

  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [showSave, setShowSave] = useState<boolean>(false);

  const isInvalid = props.invalid || (props.required && value.length === 0);

  function handleOnEnter() {
    ref.current?.blur();

    setHasChanges(false);

    setShowSave(true);
    setTimeout(() => setShowSave(false), 2000);

    props.onEnter?.(value);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter")
      handleOnEnter();
  }

  function handleOnChange(e: React.KeyboardEvent<HTMLInputElement> | React.ChangeEvent<HTMLInputElement>) {
    setHasChanges(true);
    onChange(e.currentTarget.value)
  }

  const inputClasses = {
    isInvalid: 'border-yellow bg-white',
    isDisabled: 'bg-gray-100',
    isActive: 'border-blue bg-white',
    default: 'border-gray-400 bg-white'
  }

  const currentState: InputState = isInvalid ? 'isInvalid' : props.disabled ? 'isDisabled' : isActive ? 'isActive' : 'default'

  return (
    <div className={"relative flex flex-col " + className}>
      <input
        ref={ref}
        id={props.id ?? id}
        type={isPassword ? 'password' : 'text'}
        className={`h-10 px-2 outline-none rounded-md border-2 focus:border-blue-30 ${inputClasses[currentState]} ${enterPrompt && (hasChanges || showSave) ? "pr-14" : ""} ${props.className} ${props.label ? 'mt-2' : ''}`}
        value={value}
        onChange={handleOnChange}
        onKeyDown={onKeyDown}
        autoComplete="off"
        {...rest}
      />
      {props.label && <label htmlFor={props.id ?? id} className="text-xs font-gilroy font-semibold text-gray-600 bg-white left-2 top-0 px-1 absolute w-auto">
        {props.label}
      </label>}
      {props.optional && <div className={`text-xs font-gilroy text-blue font-semibold bg-white right-2 top-0 px-1 absolute w-auto ${value.length === 0 ? "text-gray-600" : "text-gray-400"}`}>
        {t("Optional")}
      </div>}
      {props.required && <div className={`text-xs font-gilroy font-semibold bg-white right-2 top-0 px-1 absolute w-auto ${value.length === 0 ? "text-gray-600" : "text-gray-400"}`}>
        {t("Required")}
      </div>}
      {(enterPrompt && hasChanges) &&
        <div className={`flex items-center text-xs absolute right-2 ${props.label ? "top-[18px]" : "top-[10px]"} w-12 justify-center border rounded shadow px-1 pt-px bg-blue text-white cursor-pointer`} onClick={handleOnEnter}>
          Enter
        </div>
      }
      <Transition
        show={(enterPrompt && (!hasChanges && showSave)) ?? false}
        leave="transition-opacity duration-150"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className={`flex items-center text-xs absolute right-2 ${props.label ? "top-[18px]" : "top-[10px]"} w-12 justify-center border rounded shadow px-1 pt-px bg-green text-white cursor-pointer`} onClick={handleOnEnter}>
          {t("Saved")}
        </div>
      </Transition>
    </div>
  );
};

export default TextInputField;
